// Account names typed on a phone arrive with curly quotes ("Trixie’s Saloon")
// while the imported record has a straight one ("Trixie's Saloon"). Pitt,
// 2026-10-20: an account request approved under the curly name became a second
// account and a second QuickBooks customer.

/** Curly / modifier apostrophes and quotes → their straight forms. */
export function foldQuotes(name: string): string {
  return (name || '')
    .replace(/[‘’‚‛′ʼ`´]/g, "'")
    .replace(/[“”„‟″]/g, '"');
}

/** Exact-match key: quotes folded, case and surrounding/internal runs of whitespace ignored. */
export function accountNameExactKey(name: string): string {
  return foldQuotes(name).toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Loose key for "is this probably the same customer?" warnings — punctuation
 * dropped, so "Trixie's Saloon", "Trixie’s Saloon" and "Trixies Saloon" agree.
 * Never use it to pick a record automatically.
 */
export function accountNameLooseKey(name: string): string {
  return foldQuotes(name)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[!-\/:-@[-`{-~]/g, '') // ASCII punctuation (quotes already folded)
    .replace(/\s+/g, ' ')
    .trim();
}

// Words too common in customer names to mean "same customer" on their own.
const COMMON_WORDS = new Set([
  'the', 'and', 'inc', 'ltd', 'limited', 'corp', 'co', 'company', 'food', 'foods', 'fresh', 'produce',
  'market', 'markets', 'store', 'shop', 'cafe', 'restaurant', 'bar', 'grill', 'kitchen', 'deli',
  'grocer', 'grocery', 'independent', 'your', 'farm', 'farms', 'bakery', 'pizza', 'saloon', 'pub', 'house',
]);

/** Distinctive words of a name: loose-keyed, plural/possessive "s" dropped, common words removed. */
export function distinctiveWords(name: string): string[] {
  return accountNameLooseKey(name)
    .split(' ')
    .map(w => (w.length > 4 && w.endsWith('s') ? w.slice(0, -1) : w))
    .filter(w => w.length >= 4 && !COMMON_WORDS.has(w));
}

export type SameCustomerMatch<T> = { account: T; reason: 'same-name' | 'shared-word' };

/**
 * Accounts that are probably the customer behind a new account request — for
 * the admin to look at, never to pick automatically. Same name (punctuation
 * aside) first, then a shared distinctive word ("Kaufman’s YIG" → "Kaufman Your
 * Independent Grocer"; "FORTUNEXT FOOD Inc. (Osmows owensound)" → "Osmow's - Owen Sound").
 */
export function likelySameCustomer<T extends { name: string }>(requestName: string, candidates: T[]): SameCustomerMatch<T>[] {
  const key = accountNameLooseKey(requestName);
  const words = new Set(distinctiveWords(requestName));
  const same: SameCustomerMatch<T>[] = [];
  const shared: SameCustomerMatch<T>[] = [];
  for (const account of candidates) {
    if (key && accountNameLooseKey(account.name) === key) same.push({ account, reason: 'same-name' });
    else if (distinctiveWords(account.name).some(w => words.has(w))) shared.push({ account, reason: 'shared-word' });
  }
  return [...same, ...shared];
}
